import { KPIDirection, KPIUnit } from "../types";

/**
 * How each KPI is presented: the words around the number, and the two facts
 * needed to format it — `unit` and which direction counts as good.
 *
 * This lives here rather than in the API because it is presentation, not data.
 * The engine returns figures and ledger account codes; this decides how a
 * member is told what they mean, and whether a rising line should read as
 * progress. Rewording a definition is a frontend deploy, not an API change.
 *
 * It also gates the page: a KPI the API returns with no entry here is skipped,
 * so shipping a calculation before its card is written never renders a card
 * with blank prose.
 */
export interface KPIConfig {
  name: string;
  source: string;
  /** Which block of the page this card belongs to. */
  section: SectionCode;
  unit: KPIUnit;
  direction: KPIDirection;
  definition: string;
  guidance: string;
  /** Reads as the top of the fraction on the card back. */
  numeratorLabel: string;
  /**
   * The bottom of the fraction. Absent for a KPI that is a single figure
   * rather than a ratio — the card back then shows no dividing rule.
   */
  denominatorLabel?: string;
  multiplier?: string;
  /**
   * How the worked example folds in the period's length, for the two KPIs that
   * scale by it. The labels above stay prose — "× days in period" is the right
   * wording on the card's fraction — but the example needs the number, or it
   * prints a division a member cannot reproduce.
   *
   * - `multiplier` — the period scales the result: `(A ÷ B) × 396`
   * - `denominator` — the period scales the denominator: `A ÷ (B ÷ 396)`
   *
   * Absent for every other KPI, whose example already closes on its own.
   */
  periodScaling?: "multiplier" | "denominator";
  /**
   * True when the numerator is what's left of the denominator after costs
   * come out, as it is for a margin. The worked example then shows the
   * subtraction — "($262K − $155K) ÷ $262K" — instead of a single figure the
   * member would find nowhere in their books.
   */
  numeratorIsRemainder?: boolean;
  note: string;
}

export const KPI_CONFIG: Record<string, KPIConfig> = {
  field_labor_percent: {
    section: "pnl",
    name: "Field labor",
    source: "QuickBooks · P&L",
    unit: "percent",
    direction: "lower_is_better",
    definition:
      "Of every dollar you bring in, this slice pays the techs and salespeople who do the work out in the field.",
    guidance:
      "Rising usually means crews are staffed heavier than the work coming in, or pay is outrunning sales.",
    numeratorLabel: "Gross wages and burden — field techs + salespeople",
    denominatorLabel: "Total revenue",
    multiplier: "× 100",
    note: "Every dollar of pay — base, commission, spiffs, and the payroll burden that follows them — for the people who run calls and sell in the field, divided by all the revenue you collected.",
  },
  office_labor_percent: {
    section: "pnl",
    name: "Office labor",
    source: "QuickBooks · P&L",
    unit: "percent",
    direction: "lower_is_better",
    definition:
      "The share of revenue spent on you and the office team — the people who keep the business running behind the scenes.",
    guidance:
      "If it creeps up over time, office payroll is growing faster than the company is.",
    numeratorLabel: "Gross wages and burden — owner + office team",
    denominatorLabel: "Total revenue",
    multiplier: "× 100",
    note: "Pay for you and the back-office crew — CSRs, dispatch, admin — plus the payroll burden that follows those wages, divided by all the revenue you collected.",
  },
  gross_margin_percent: {
    section: "pnl",
    name: "Gross margin",
    source: "QuickBooks · P&L",
    unit: "percent",
    direction: "higher_is_better",
    definition:
      "What's left from each job after you pay for the labor and materials to do it — before overhead.",
    guidance:
      "This is your engine. A steady or rising line means your pricing and job costs are in good shape.",
    numeratorLabel: "Total revenue − direct costs (labor + materials)",
    denominatorLabel: "Total revenue",
    multiplier: "× 100",
    numeratorIsRemainder: true,
    note: "Start with revenue, then take out the labor and parts it took to do the jobs — what's left is your gross margin. Overhead like office pay, marketing and rent isn't taken out here; that comes off at net profit.",
  },
  net_profit_percent: {
    section: "pnl",
    name: "Net profit",
    source: "QuickBooks · P&L",
    unit: "percent",
    direction: "higher_is_better",
    definition:
      "The money you actually keep after everything is paid — your true bottom line.",
    guidance:
      "A dip here means costs grew faster than sales somewhere above. Trace it back through the other P&L cards.",
    numeratorLabel: "Gross margin − operating expenses ± other",
    denominatorLabel: "Total revenue",
    multiplier: "× 100",
    note: "Take your gross margin and subtract everything else it costs to keep the doors open — rent, trucks, insurance, marketing, office pay. Then anything from outside the day-to-day, like interest or a one-off gain, is added or taken off. What's left is your net income, the same figure your QuickBooks P&L ends on.",
  },
  materials_equipment_percent: {
    section: "pnl",
    name: "Materials & equipment",
    source: "QuickBooks · P&L",
    unit: "percent",
    direction: "lower_is_better",
    definition:
      "The portion of revenue spent on the parts and equipment you install on jobs.",
    guidance:
      "Holding steady is what you want. Spikes can mean rising supplier prices, wasted material, or jobs priced too low for what they cost.",
    numeratorLabel: "Parts and equipment installed on jobs",
    denominatorLabel: "Total revenue",
    multiplier: "× 100",
    note: "What you paid your suppliers for the materials and equipment that went into the work — divided by all the revenue you collected. Labor to install it isn't in here; that's field labor.",
  },
  accounts_receivable: {
    section: "balance_sheet",
    name: "Accounts receivable",
    source: "QuickBooks · Balance Sheet",
    unit: "currency",
    direction: "lower_is_better",
    definition:
      "Money your customers still owe you for work you've already finished.",
    guidance:
      "Lower and flatter is better. A climbing line is cash stuck in unpaid invoices instead of your bank.",
    numeratorLabel:
      "The closing balance of the accounts you mapped to Accounts Receivable",
    note: "Every open invoice your customers haven't paid yet, as of the last day of the month. Money you've collected but not yet deposited isn't counted here.",
  },
  days_sales_outstanding: {
    section: "balance_sheet",
    name: "Days sales outstanding",
    source: "QuickBooks · A/R + Sales",
    unit: "days",
    direction: "lower_is_better",
    definition:
      "How long, on average, it takes to actually collect after a job is done — your A/R balance measured in time.",
    guidance:
      "Creeping up means cash is sitting in unpaid invoices longer. Same story as your A/R balance, measured in time.",
    numeratorLabel: "Accounts receivable",
    denominatorLabel: "Total sales",
    multiplier: "× days in period",
    periodScaling: "multiplier",
    // Deliberately "total sales", not the textbook "total credit sales".
    // QuickBooks' P&L cannot separate work billed on credit from a job paid at
    // the door, so this divides by everything sold — which reads faster than
    // reality, most so for members who collect on completion. Saying "credit
    // sales" would claim a split we did not compute.
    note: "Take what customers still owe you, divide by everything you sold over the period, then multiply by the days in it. Lower means you turn finished work into cash faster.",
  },
  days_cash_on_hand: {
    section: "balance_sheet",
    name: "Days cash on hand",
    source: "QuickBooks · Balance Sheet + P&L",
    unit: "days",
    direction: "higher_is_better",
    definition:
      "How many days you could keep paying the bills from cash on hand if no new money came in — your runway.",
    guidance:
      "More days of cash means you can ride out a slow month without scrambling or borrowing.",
    numeratorLabel: "Cash & cash equivalents",
    denominatorLabel: "Operating expenses ÷ days in period",
    periodScaling: "denominator",
    // Depreciation cannot be excluded: our ledger catalog has no account for
    // it, so it sits inside other overhead. The burn rate is therefore a little
    // high and the runway a little short — an error in the safe direction, but
    // the note below does not repeat the prototype's claim that non-cash items
    // are left out, because they are not.
    note: "Cash in your accounts divided by your average daily operating spend. Materials and field labour aren't counted — with no work coming in you'd stop paying those too.",
  },
  retained_earnings: {
    section: "balance_sheet",
    name: "Retained earnings",
    source: "QuickBooks · Balance Sheet",
    unit: "currency",
    direction: "higher_is_better",
    definition:
      "All the profit your company has kept and reinvested since the day it opened.",
    guidance:
      "A steady climb means you're building real, lasting value — not just covering this month's bills.",
    numeratorLabel:
      "All-time net profit kept in the business, minus any owner draws",
    note: "It grows every month you turn a profit and leave that money in the company instead of taking it out. This year's profit counts as it accrues, rather than waiting for the books to close.",
  },
  debt_to_equity: {
    section: "balance_sheet",
    name: "Debt to equity",
    source: "QuickBooks · Balance Sheet",
    unit: "ratio",
    direction: "lower_is_better",
    definition:
      "How much of the business is funded by borrowing versus your own equity. Below 1.0 means you own more than you owe.",
    guidance:
      "Trending down as you pay debt off — you're funding the company with your own equity, not the bank's. Above 1.0 is the flag you're surviving on debt.",
    numeratorLabel: "Total liabilities",
    denominatorLabel: "Total equity",
    note: "Everything the company owes — loans, lines of credit, financed equipment — divided by owner's equity. Some coaches use only interest-bearing debt for a cleaner read on leverage.",
  },
};

export type SectionCode = "pnl" | "balance_sheet";

export interface DashboardSectionConfig {
  ordinal: string;
  title: string;
  source: string;
  blurb: string;
}

/**
 * The page's blocks, in the order they render.
 *
 * A section with no KPIs is not rendered at all, so adding one here ahead of
 * its calculations does not leave an empty heading on the page.
 */
export const SECTIONS: Record<SectionCode, DashboardSectionConfig> = {
  pnl: {
    ordinal: "01",
    title: "Profit & Loss",
    source: "QuickBooks",
    blurb:
      "How your money moves through the business each month — what comes in, what it costs to do the work, and what you keep.",
  },
  balance_sheet: {
    ordinal: "02",
    title: "Balance Sheet",
    source: "QuickBooks",
    blurb:
      "The bigger picture of the company's financial health — what it owns and owes, and how fast it collects.",
  },
};
